/* eslint-disable */
/**
 * BankAgentTab — Agent Wyciągów (ujednolicony model, 2026-02).
 *
 * Filozofia: JEDYNY sposób uruchamiania analizy = checkbox
 * „Automatycznie analizuj nowych kontrahentów" (backend job w tle).
 * Każda karta kontrahenta ma dodatkowo akcję „Sprawdź ponownie" —
 * pojedyncza analiza tego kontrahenta z pominięciem cache.
 *
 * Plus: rozwijanie szczegółów per kontrahent — pełne tytuły transakcji
 * (z numerami faktur, w całości, bez przycinania).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Sparkles, RefreshCw, CheckCircle2, AlertCircle, Clock, Inbox,
  ChevronDown, ChevronRight, XCircle, Search, X as XIcon, ArrowUpAZ, ArrowDownAZ,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useBankAutoAnalysisJob } from "@/hooks/useBankAutoAnalysisJob";
import { useInvoicePreview } from "@/contexts/InvoicePreviewContext";
import AgentDebugPanel from "@/components/agent-debug/AgentDebugPanel";
import LearnedIbansChips from "@/components/agent-debug/LearnedIbansChips";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METHOD_LABEL = {
  transfer: "Przelew", card: "Karta", blik: "BLIK", cash: "Gotówka",
  online: "Płatność online", online_payment: "Płatność online",
  other_non_transfer: "Inna forma (nie-przelew)",
  mixed: "Mieszane", unknown: "Nieznana",
};

const STATUS_META = {
  not_analyzed: { label: "Do analizy", icon: Clock, cls: "bg-gray-100 text-gray-700 border-gray-300" },
  analyzing: { label: "Analizuję", icon: Loader2, cls: "bg-blue-50 text-blue-700 border-blue-300 animate-pulse", spin: true },
  analyzed: { label: "Przeanalizowany", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  cached: { label: "Cache", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  needs_review: { label: "Wymaga uwagi", icon: AlertCircle, cls: "bg-orange-50 text-orange-700 border-orange-300" },
  failed: { label: "Błąd", icon: XCircle, cls: "bg-red-50 text-red-700 border-red-300" },
};

const formatPLN = (v) =>
  `${Number(v).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN`;

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.not_analyzed;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}>
      <Icon className={`w-3 h-3 ${m.spin ? "animate-spin" : ""}`} />{m.label}
    </span>
  );
};

const HintBadges = ({ hints }) => {
  const entries = Object.entries(hints || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="flex gap-1 flex-wrap">
      {entries.map(([k, n]) => (
        <span key={k} className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
          {METHOD_LABEL[k] || k} · {n}
        </span>
      ))}
    </div>
  );
};

// Szczegóły transakcji per kontrahent (expandable row content)
const CounterpartyTransactions = ({ counterpartyKey }) => {
  const [txs, setTxs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openInvoice } = useInvoicePreview();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/ai/agents/bank/counterparties/${encodeURIComponent(counterpartyKey)}/transactions`,
          { withCredentials: true }
        );
        if (alive) setTxs(r.data?.transactions || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Błąd pobierania transakcji.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [counterpartyKey]);

  if (loading) {
    return <div className="text-xs text-gray-500 px-3 py-2"><Loader2 className="w-3 h-3 inline animate-spin mr-1.5" />Ładowanie transakcji…</div>;
  }
  if (error) return <div className="text-xs text-red-600 px-3 py-2">{error}</div>;
  if (!txs?.length) return <div className="text-xs text-gray-500 px-3 py-2">Brak transakcji.</div>;

  // Pakiet BANK-AGENT-PREVIEW (2026-02-24): klikalne numery faktur wykryte
  // w tytule przelewu. Backend zwraca `matched_invoices` per transakcja.
  // Klik → istniejący globalny useInvoicePreview().openInvoice() — ten sam
  // podgląd używany w Fakturach, KSeF Inbox i Agencie Faktur.
  return (
    <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/60" data-testid={`bank-agent-cp-tx-list-${counterpartyKey}`}>
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">
        Transakcje źródłowe ({txs.length}) — tytuły w całości
      </div>
      <div className="space-y-1">
        {txs.map((t) => (
          <div key={t.id} className="grid grid-cols-12 gap-2 text-xs py-1.5 border-b border-gray-100 last:border-b-0" data-testid={`bank-agent-cp-tx-${t.id}`}>
            <div className="col-span-2 text-gray-600 tabular-nums">{t.booking_date || "—"}</div>
            <div className="col-span-2 text-right font-medium tabular-nums text-red-700">{formatPLN(t.amount)}</div>
            <div className="col-span-1">
              {t.payment_method_hint && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                  {METHOD_LABEL[t.payment_method_hint] || t.payment_method_hint}
                </span>
              )}
            </div>
            <div className="col-span-3 text-gray-500 font-mono text-xs break-all" title={t.counterparty_iban || ""}>
              {t.counterparty_iban || "—"}
            </div>
            <div className="col-span-4 text-gray-800 break-words whitespace-pre-wrap" data-testid={`bank-agent-cp-tx-title-${t.id}`}>
              {t.title || <span className="text-gray-400">—</span>}
              {Array.isArray(t.matched_invoices) && t.matched_invoices.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`bank-agent-cp-tx-matches-${t.id}`}>
                  {t.matched_invoices.map((inv) => (
                    <button
                      key={inv.invoice_id || inv.ksef_number || inv.invoice_number}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openInvoice(inv);
                      }}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 font-mono text-xs"
                      title={`Otwórz podgląd faktury ${inv.invoice_number}`}
                      data-testid={`bank-agent-cp-tx-match-preview-${t.id}-${inv.invoice_id || inv.ksef_number}`}
                    >
                      <Search className="w-3 h-3" />
                      {inv.invoice_number}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BankAgentTab = () => {
  const [counterparties, setCounterparties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());   // counterparty_keys
  const [busyKeys, setBusyKeys] = useState(() => new Set());   // per-card "Sprawdź ponownie"
  // Log ostatniej analizy Agenta Wyciągów — tylko w stanie UI, nie w MongoDB.
  const [lastDebugInfo, setLastDebugInfo] = useState(null);
  // Setting persistowany w `/api/agent-settings` (round unify 2026-02).
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoBusy, setAutoBusy] = useState(true);
  // Wyszukiwarka w stylu bankowym: dropdown sugestii po ≥3 znakach.
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  // Sortowanie po nazwie kontrahenta (asc = A-Z, desc = Z-A).
  const [sortDir, setSortDir] = useState("asc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/ai/agents/bank/counterparties`,
        { withCredentials: true }
      );
      setCounterparties(res.data?.counterparties || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się pobrać kontrahentów.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Backend-driven auto-analysis job (2026-02-17, ujednolicone 2026-02).
  // Analiza działa po stronie backendu nawet gdy user zamknie stronę.
  // Frontend tylko start/cancel + polling. Trigger = checkbox
  // „Automatycznie analizuj nowych kontrahentów".
  const autoJob = useBankAutoAnalysisJob({
    onComplete: (final) => {
      const msg = `Analiza zakończona: ${final.analyzed} zaktualizowano, ${final.cached} z cache, ${final.failed} błędów`;
      if (final.status === "completed") {
        if (final.failed > 0) toast.warning(msg);
        else toast.success(msg);
      } else if (final.status === "cancelled") {
        toast.info(`Analiza anulowana po ${final.processed}/${final.total}`);
      } else if (final.status === "failed") {
        toast.error(msg);
      }
      fetchData();
    },
  });

  const unanalyzedCount = counterparties.filter(
    (c) => c.profile_status === "not_analyzed"
  ).length;

  // Fetch aktualnego stanu checkboxa z backendu (setting persistowany).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/agent-settings`, { withCredentials: true });
        if (!alive) return;
        setAutoEnabled(!!(r.data?.bank_agent?.automatically_analyze_new_counterparties));
      } catch (_e) {
        if (alive) setAutoEnabled(false);
      } finally {
        if (alive) setAutoBusy(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleToggleAuto = useCallback(async (checked) => {
    setAutoBusy(true);
    setAutoEnabled(!!checked);  // optimistic
    try {
      await axios.put(
        `${API}/agent-settings`,
        { bank_agent: { automatically_analyze_new_counterparties: !!checked } },
        { withCredentials: true },
      );
      if (checked) {
        // Po włączeniu — od razu spróbuj przeanalizować nieprzeanalizowanych.
        try { await autoJob.start(); } catch (_e) { /* silent */ }
      } else if (autoJob.running) {
        // Po wyłączeniu — anuluj aktywną analizę (jeśli trwa).
        try { await autoJob.cancel(); } catch (_e) { /* silent */ }
      }
    } catch (_e) {
      setAutoEnabled(!checked);
      toast.error("Nie udało się zapisać ustawienia.");
    } finally {
      setAutoBusy(false);
    }
  }, [autoJob]);

  const toggleExpand = (key) => {
    setExpanded((s) => {
      const ns = new Set(s);
      if (ns.has(key)) ns.delete(key); else ns.add(key);
      return ns;
    });
  };

  // Wyszukiwarka + sortowanie A-Z / Z-A po nazwie kontrahenta.
  // Filter tylko gdy searchTerm nie jest pusty (od 1 znaku — pełny filtr listy).
  // Dropdown sugestii pod inputem — pojawia się dopiero po ≥3 znakach.
  const searchQ = searchTerm.trim().toLowerCase();
  const nameOf = (c) => (c.counterparty_name || "").toLowerCase();
  const items = [...counterparties]
    .filter((c) => (searchQ ? nameOf(c).includes(searchQ) : true))
    .sort((a, b) => {
      const cmp = (a.counterparty_name || "").localeCompare(
        b.counterparty_name || "", "pl", { sensitivity: "base" }
      );
      return sortDir === "asc" ? cmp : -cmp;
    });

  // Sugestie: top 5 pasujących kontrahentów (uporządkowane A-Z niezależnie
  // od `sortDir`, żeby lista sugestii była przewidywalna).
  const suggestions = searchQ.length >= 3
    ? [...counterparties]
        .filter((c) => nameOf(c).includes(searchQ))
        .sort((a, b) => (a.counterparty_name || "").localeCompare(
          b.counterparty_name || "", "pl", { sensitivity: "base" }
        ))
        .slice(0, 5)
    : [];

  // Status per row uwzględniający lokalną analizę „Sprawdź ponownie".
  const computeRowStatus = (c) => {
    const k = c.counterparty_key;
    if (busyKeys.has(k)) return "analyzing";
    return c.profile_status;
  };

  const stats = {
    total: counterparties.length,
    analyzed: counterparties.filter((c) => c.profile_status === "analyzed").length,
    review: counterparties.filter((c) => c.profile_status === "needs_review").length,
  };

  if (loading) {
    return <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Ładowanie kontrahentów…</div>;
  }
  if (!counterparties.length) {
    return (
      <Card className="border-dashed border-gray-300 bg-gray-50" data-testid="bank-agent-cp-empty">
        <CardContent className="p-6 flex items-start gap-3">
          <Inbox className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-gray-800">Brak kontrahentów do analizy.</p>
            <p className="text-gray-600 text-xs mt-0.5">Lista jest budowana z transakcji wychodzących (amount &lt; 0) z zapisanych wyciągów.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="bank-agent-tab">
      {/* Status header */}
      <Card className="border border-gray-200">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-gray-900">Kontrahenci do analizy</h3>
          <p className="text-xs text-gray-500 mt-1">
            Agent przetwarza kontrahentów <strong>po kolei</strong> — każdy kontrahent
            dostaje osobny prompt i zapisywany jest jako osobny profil.
          </p>
          <dl className="grid grid-cols-3 gap-2 mt-3 text-xs">
            <div><dt className="text-gray-500">Łącznie</dt><dd className="font-semibold text-gray-900" data-testid="bank-agent-stat-total">{stats.total}</dd></div>
            <div><dt className="text-gray-500">Przeanalizowani</dt><dd className="font-semibold text-emerald-700" data-testid="bank-agent-stat-analyzed">{stats.analyzed}</dd></div>
            <div><dt className="text-gray-500">Wymagają uwagi</dt><dd className="font-semibold text-orange-700" data-testid="bank-agent-stat-review">{stats.review}</dd></div>
          </dl>
        </CardContent>
      </Card>

      {/* Karta „Analiza AI Wyciągów" — zawsze widoczna (standard PAX Agent UX).
          Sekcja progress renderuje się warunkowo w środku. */}
      <Card
        className="border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-violet-50/30"
        data-testid="bank-agent-analysis-card"
      >
        <CardContent className="p-4 sm:p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg bg-white shadow-sm border border-indigo-100 shrink-0">
              <Sparkles className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base sm:text-lg text-gray-900">
                Analiza AI Wyciągów
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Buduj profile kontrahentów na podstawie historii transakcji bankowych.
              </p>
            </div>
            {autoJob.running && (
              <Button
                size="sm"
                variant="outline"
                onClick={autoJob.cancel}
                disabled={autoJob.cancelRequested}
                data-testid="bank-agent-auto-stop"
                className="border-rose-300 text-rose-700 hover:bg-rose-50 shrink-0 disabled:opacity-70"
              >
                {autoJob.cancelRequested
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <XCircle className="w-3.5 h-3.5 mr-1.5" />}
                {autoJob.cancelRequested ? "Anulowanie…" : "Anuluj analizę"}
              </Button>
            )}
          </div>

          {/* Checkbox: „Automatycznie analizuj nowych kontrahentów" —
              JEDYNY sposób uruchamiania analizy (2026-02 unify).
              Setting persistowany w /api/agent-settings. */}
          <div
            className="flex items-center gap-2 text-xs sm:text-sm text-gray-700"
            data-testid="bank-agent-autoanalyze-toggle-wrap"
          >
            <input
              type="checkbox"
              id="bank-agent-autoanalyze-toggle"
              data-testid="bank-agent-autoanalyze-toggle"
              checked={!!autoEnabled}
              disabled={!!autoBusy}
              onChange={(e) => handleToggleAuto(e.target.checked)}
              className="cursor-pointer disabled:opacity-50 disabled:cursor-wait"
            />
            <label
              htmlFor="bank-agent-autoanalyze-toggle"
              className="cursor-pointer select-none"
            >
              Automatycznie analizuj nowych kontrahentów
            </label>
            {autoBusy ? <Loader2 className="w-3 h-3 animate-spin text-gray-400" /> : null}
            {!autoBusy && !autoJob.running && unanalyzedCount > 0 ? (
              <span className="text-gray-500 text-xs ml-1">
                (do analizy: <strong className="text-gray-700">{unanalyzedCount}</strong>)
              </span>
            ) : null}
          </div>

          {autoJob.running && (
            <>
              {/* Header progresu + licznik */}
              <div className="flex items-center justify-between text-xs text-gray-600 gap-3">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                  <span className="shrink-0">Analizowanie kontrahenta</span>
                </div>
                <span
                  className="font-mono tabular-nums shrink-0"
                  data-testid="bank-agent-analyze-progress-counter"
                >
                  {autoJob.progress.current} / {autoJob.progress.total}
                </span>
              </div>

              {/* Liniowy pasek postępu */}
              <div
                className="h-1.5 bg-indigo-100 rounded overflow-hidden"
                data-testid="bank-agent-analyze-progress-bar"
              >
                <div
                  className="h-full bg-indigo-600 transition-all duration-300 ease-out"
                  style={{
                    width: `${
                      autoJob.progress.total > 0
                        ? Math.max(0, Math.min(100, (autoJob.progress.current / autoJob.progress.total) * 100))
                        : 0
                    }%`,
                  }}
                />
              </div>

              {/* Aktualny kontrahent — PEŁNA nazwa bez truncate. Klucz Bank
                  Agenta ma format „nip|iban|name" — rozdzielamy na segmenty
                  i etykietujemy. Nazwa może zajmować dowolną liczbę linii. */}
              {autoJob.currentName && (() => {
                const parts = String(autoJob.currentName).split("|");
                const nip = parts[0] && parts[0] !== "null" ? parts[0].trim() : "";
                const iban = parts[1] && parts[1] !== "null" ? parts[1].trim() : "";
                const name = parts.slice(2).join("|").trim();
                return (
                  <div
                    className="text-xs sm:text-sm text-gray-800 space-y-0.5"
                    data-testid="bank-agent-analyze-current-item"
                  >
                    <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                      Aktualnie analizowany kontrahent
                    </div>
                    {name && (
                      <div className="font-semibold break-words whitespace-normal">
                        {name}
                      </div>
                    )}
                    {!name && !nip && !iban && (
                      <div className="font-mono text-gray-700 break-all whitespace-normal">
                        {autoJob.currentName}
                      </div>
                    )}
                    {nip && (
                      <div className="text-xs text-gray-600 font-mono">
                        NIP: {nip}
                      </div>
                    )}
                    {iban && (
                      <div className="text-xs text-gray-600 font-mono break-all">
                        IBAN: {iban}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Statystyki inline */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="text-emerald-700">
                  ✓ Zaktualizowane: <strong data-testid="bank-agent-analyze-analyzed">{autoJob.analyzed}</strong>
                </span>
                <span className="text-emerald-700">
                  ↻ Z cache: <strong data-testid="bank-agent-analyze-cached">{autoJob.cached}</strong>
                </span>
                <span className="text-red-700">
                  ✗ Błędy: <strong data-testid="bank-agent-analyze-failed">{autoJob.failed}</strong>
                </span>
              </div>

              {/* Info-banner (niebieski) — standard PAX */}
              <div
                className="rounded border border-indigo-200 bg-indigo-50 text-indigo-900 text-xs p-3 flex items-start gap-2"
                data-testid="bank-agent-analyze-info-banner"
              >
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-indigo-600" />
                <div>
                  <div className="font-semibold">Analiza działa w tle.</div>
                  <div>Możesz zamknąć przeglądarkę. Postęp zostanie zapisany.</div>
                </div>
              </div>

              {/* Ostatnia aktualizacja */}
              {autoJob.lastUpdatedAt && (
                <div className="text-xs text-gray-500" data-testid="bank-agent-analyze-last-updated">
                  Ostatnia aktualizacja: {new Date(autoJob.lastUpdatedAt).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}

              {/* Banner anulowania (rose, zgodnie ze standardem) */}
              {autoJob.cancelRequested && (
                <div
                  className="rounded border border-rose-200 bg-rose-50 text-rose-900 text-xs px-2 py-1"
                  data-testid="bank-agent-analyze-cancel-banner"
                >
                  Zatrzymywanie po bieżącym kontrahencie…
                </div>
              )}

              {/* Ostatni błąd inline — pełne teksty bez obcinania */}
              {autoJob.lastError && (
                <div className="text-xs text-red-700 break-words" data-testid="bank-agent-analyze-last-error">
                  Ostatni błąd:{" "}
                  <strong className="break-all">{autoJob.lastError.counterparty_key}</strong>
                  {" — "}
                  <span className="break-words">{autoJob.lastError.error}</span>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Toolbar poniżej „Analiza AI Wyciągów": wyszukiwarka (banking-style
          autocomplete po ≥3 znakach) + sortowanie A-Z / Z-A po nazwie
          kontrahenta. */}
      <Card className="border border-gray-200" data-testid="bank-agent-search-sort">
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1 min-w-0" data-testid="bank-agent-search-wrapper">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setSuggestionsOpen(true); }}
                onFocus={() => setSuggestionsOpen(true)}
                onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
                placeholder="Szukaj kontrahenta…"
                data-testid="bank-agent-search-input"
                className="w-full h-9 pl-8 pr-8 rounded border border-gray-300 bg-white text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                autoComplete="off"
                spellCheck={false}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => { setSearchTerm(""); setSuggestionsOpen(false); }}
                  data-testid="bank-agent-search-clear"
                  aria-label="Wyczyść wyszukiwarkę"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              )}

              {/* Banking-style dropdown sugestii (top 5) po ≥3 znakach. */}
              {suggestionsOpen && suggestions.length > 0 && (
                <ul
                  className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
                  data-testid="bank-agent-search-suggestions"
                >
                  {suggestions.map((s) => (
                    <li key={s.counterparty_key}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} // zapobiega blur inputa
                        onClick={() => {
                          setSearchTerm(s.counterparty_name || "");
                          setSuggestionsOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-indigo-50 flex items-center justify-between gap-3"
                        data-testid={`bank-agent-search-suggestion-${s.counterparty_key}`}
                      >
                        <span className="truncate">{s.counterparty_name}</span>
                        {s.profile?.payment_method ? (
                          <span className="text-xs text-gray-500 shrink-0">
                            {METHOD_LABEL[s.profile.payment_method] || s.profile.payment_method}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Sortowanie A-Z / Z-A — jeden przycisk toggle. */}
            <button
              type="button"
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              data-testid="bank-agent-sort-toggle"
              aria-label={sortDir === "asc" ? "Sortuj Z-A" : "Sortuj A-Z"}
              className="h-9 px-3 rounded border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 shrink-0"
              title={sortDir === "asc" ? "Sortuj Z-A" : "Sortuj A-Z"}
            >
              {sortDir === "asc"
                ? <ArrowUpAZ className="w-4 h-4 text-gray-500" />
                : <ArrowDownAZ className="w-4 h-4 text-gray-500" />}
              <span>Sortuj {sortDir === "asc" ? "A-Z" : "Z-A"}</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Log ostatniej analizy Agenta Wyciągów */}
      {lastDebugInfo && (
        <AgentDebugPanel
          title="Log ostatniej analizy Agenta Wyciągów"
          debugInfo={lastDebugInfo}
          testIdPrefix="bank-debug"
          summaryRows={[
            {
              label: "Kontrahent",
              value: lastDebugInfo.entity_name || lastDebugInfo.entity_id || "—",
            },
            {
              label: "Liczba transakcji",
              value: lastDebugInfo.input_payload?.transactions_count
                ?? lastDebugInfo.aggregation?.transactions_fetched ?? "—",
            },
            {
              label: "Unikalne IBAN-y",
              value: lastDebugInfo.aggregation?.distinct_counterparty_ibans_count ?? 0,
            },
            {
              label: "Najczęstszy IBAN",
              value: lastDebugInfo.aggregation?.most_common_iban || "—",
            },
            {
              label: "IBAN zapisany",
              value: lastDebugInfo.saved_profile?.counterparty_iban || "—",
              accent: lastDebugInfo.saved_profile?.counterparty_iban
                ? "text-emerald-700" : "text-rose-700",
            },
            {
              label: "ibans w profilu",
              value: (() => {
                const arr = lastDebugInfo.saved_profile?.ibans || [];
                return Array.isArray(arr) ? arr.length : 0;
              })(),
            },
            {
              label: "iban_learning_status",
              value: lastDebugInfo.saved_profile?.iban_learning_status || "—",
              accent: (() => {
                const s = lastDebugInfo.saved_profile?.iban_learning_status;
                if (s === "stable") return "text-emerald-700";
                if (s === "no_iban") return "text-rose-700";
                if (s === "multiple_ibans" || s === "new_iban_detected") return "text-amber-700";
                return "text-gray-700";
              })(),
            },
            {
              label: "payment_method",
              value: lastDebugInfo.saved_profile?.payment_method || "—",
            },
            {
              label: "Pewność",
              value: typeof lastDebugInfo.saved_profile?.confidence === "number"
                ? `${Math.round(lastDebugInfo.saved_profile.confidence * 100)}%`
                : "—",
            },
            {
              label: "Status zapisu",
              value: lastDebugInfo.aggregation?.save_status || "—",
            },
          ]}
        />
      )}

      {/* Counterparty list */}
      <div className="space-y-2 landscape-card-grid" data-testid="bank-agent-cp-list">
        {items.map((c, idx) => {
          const prof = c.profile;
          const k = c.counterparty_key;
          const rowStatus = computeRowStatus(c);
          const isExpanded = expanded.has(k);
          const isBusy = busyKeys.has(k);

          return (
            <Card key={k} className="border border-gray-200" data-testid={`bank-agent-cp-row-${idx}`}>
              <CardContent className="p-3">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <button
                    type="button"
                    onClick={() => toggleExpand(k)}
                    className="text-gray-400 hover:text-gray-700 shrink-0 mt-0.5"
                    aria-label={isExpanded ? "Zwiń" : "Rozwiń szczegóły"}
                    data-testid={`bank-agent-cp-expand-${idx}`}
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 text-sm break-words" title={c.counterparty_name}>{c.counterparty_name}</span>
                      <StatusBadge status={rowStatus} />
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>Wydatki: <strong className={`tabular-nums ${c.total_amount < 0 ? "text-red-700" : "text-gray-700"}`}>{formatPLN(c.total_amount)}</strong></span>
                      <span>Transakcji: <strong className="text-gray-700">{c.transactions_count}</strong></span>
                      <span>Ostatnia: <strong className="text-gray-700">{c.last_seen_at || "—"}</strong></span>
                    </div>
                    <div className="mt-1.5"><HintBadges hints={c.payment_method_hints} /></div>
                    {prof && (
                      <div className="text-xs mt-1.5 text-emerald-800" data-testid={`bank-agent-cp-profile-${idx}`}>
                        Profil: <strong>{METHOD_LABEL[prof.payment_method] || prof.payment_method}</strong> · pewność {(Number(prof.confidence) * 100).toFixed(0)}%
                      </div>
                    )}
                    {prof && (
                      <LearnedIbansChips
                        ibans={prof.ibans}
                        currentIban={prof.counterparty_iban}
                        history={prof.iban_history}
                        learningStatus={prof.iban_learning_status}
                        testIdPrefix={`bank-agent-cp-ibans-${idx}`}
                      />
                    )}
                  </div>

                  {/* „Sprawdź ponownie" — pojedyncza analiza tego kontrahenta
                      (force=true). Zawsze widoczna. Wyłączona podczas
                      lokalnej analizy lub aktywnego job-a auto-analizy. */}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isBusy || autoJob.running}
                    onClick={async () => {
                      setBusyKeys((s) => new Set(s).add(k));
                      try {
                        const respAnalyze = await axios.post(
                          `${API}/ai/agents/bank/analyze-counterparty`,
                          { counterparty_key: k, force: true },
                          { withCredentials: true }
                        );
                        if (respAnalyze?.data?.debug_info) {
                          setLastDebugInfo(respAnalyze.data.debug_info);
                        }
                        toast.success("Profil odświeżony.");
                        await fetchData();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Błąd analizy.");
                      } finally {
                        setBusyKeys((s) => { const ns = new Set(s); ns.delete(k); return ns; });
                      }
                    }}
                    data-testid={`bank-agent-cp-reanalyze-${idx}`}
                    className="shrink-0"
                  >
                    {isBusy
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Sprawdź ponownie
                  </Button>
                </div>
              </CardContent>
              {isExpanded && <CounterpartyTransactions counterpartyKey={k} />}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default BankAgentTab;
